import React, { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface RateLimitConfig {
  limits: Record<string, { requests: number; window: number }>
  description: Record<string, string>
}

interface BlockedIP {
  ip: string
  blocked_until: string
  remaining_seconds: number
}

interface Statistics {
  time_range_minutes: number
  total_requests: number
  allowed_requests: number
  blocked_requests: number
  block_rate: number
  by_endpoint: Record<string, { total: number; allowed: number; blocked: number }>
  top_blocked_identifiers: Array<{ identifier: string; blocked_count: number }>
  currently_blocked_ips: number
  active_buckets: number
}

const API_BASE = '/api/rate-limit'

const RateLimitDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'config' | 'blocked' | 'users'>('overview')
  const [config, setConfig] = useState<RateLimitConfig | null>(null)
  const [blockedIPs, setBlockedIPs] = useState<BlockedIP[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [timeRange, setTimeRange] = useState(60)

  // Edit state
  const [editingEndpoint, setEditingEndpoint] = useState<string | null>(null)
  const [editRequests, setEditRequests] = useState(0)
  const [editWindow, setEditWindow] = useState(0)

  // Block IP state
  const [blockIP, setBlockIP] = useState('')
  const [blockDuration, setBlockDuration] = useState(60)

  useEffect(() => {
    loadData()
  }, [timeRange])

  const loadData = async () => {
    setLoading(true)
    try {
      const [configRes, blockedRes, statsRes] = await Promise.all([
        fetch(`${API_BASE}/config`),
        fetch(`${API_BASE}/blocked`),
        fetch(`${API_BASE}/statistics?time_range_minutes=${timeRange}`)
      ])

      if (configRes.ok) setConfig(await configRes.json())
      if (blockedRes.ok) setBlockedIPs(await blockedRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (err) {
      setError('Failed to load rate limit data')
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateConfig = async (endpoint: string) => {
    try {
      const res = await fetch(`${API_BASE}/config/${endpoint}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ requests: editRequests, window: editWindow })
      })

      if (res.ok) {
        setEditingEndpoint(null)
        loadData()
      }
    } catch (err) {
      setError('Failed to update configuration')
    }
  }

  const handleBlockIP = async () => {
    if (!blockIP) return

    try {
      const res = await fetch(`${API_BASE}/block/${encodeURIComponent(blockIP)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ duration_minutes: blockDuration })
      })

      if (res.ok) {
        setBlockIP('')
        loadData()
      }
    } catch (err) {
      setError('Failed to block IP')
    }
  }

  const handleUnblockIP = async (ip: string) => {
    try {
      const res = await fetch(`${API_BASE}/block/${encodeURIComponent(ip)}`, {
        method: 'DELETE'
      })

      if (res.ok) {
        loadData()
      }
    } catch (err) {
      setError('Failed to unblock IP')
    }
  }

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const secs = seconds % 60
    if (minutes > 0) return `${minutes}m ${secs}s`
    return `${secs}s`
  }

  const styles: Record<string, React.CSSProperties> = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#f5f7fa'
    },
    content: {
      padding: '32px',
      maxWidth: '1400px',
      margin: '0 auto'
    },
    title: {
      fontSize: '28px',
      fontWeight: 700,
      color: '#1F2937',
      marginBottom: '8px'
    },
    subtitle: {
      color: '#6B7280',
      marginBottom: '24px'
    },
    tabs: {
      display: 'flex',
      gap: '8px',
      marginBottom: '24px',
      borderBottom: '1px solid #E5E7EB',
      paddingBottom: '8px'
    },
    tab: {
      padding: '10px 20px',
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 500,
      color: '#6B7280',
      borderRadius: '8px 8px 0 0'
    },
    activeTab: {
      backgroundColor: '#4A90E2',
      color: 'white'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px',
      marginBottom: '24px'
    },
    statCard: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    statValue: {
      fontSize: '28px',
      fontWeight: 700,
      color: '#1F2937'
    },
    statLabel: {
      fontSize: '14px',
      color: '#6B7280',
      marginTop: '4px'
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      marginBottom: '24px'
    },
    cardTitle: {
      fontSize: '18px',
      fontWeight: 600,
      color: '#1F2937',
      marginBottom: '16px'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse' as const
    },
    th: {
      textAlign: 'left' as const,
      padding: '12px 16px',
      borderBottom: '2px solid #E5E7EB',
      fontSize: '12px',
      fontWeight: 600,
      color: '#6B7280',
      textTransform: 'uppercase' as const
    },
    td: {
      padding: '12px 16px',
      borderBottom: '1px solid #E5E7EB'
    },
    input: {
      padding: '8px 12px',
      border: '1px solid #D1D5DB',
      borderRadius: '6px',
      fontSize: '14px',
      width: '100px'
    },
    button: {
      padding: '8px 16px',
      backgroundColor: '#4A90E2',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 500
    },
    dangerButton: {
      padding: '8px 16px',
      backgroundColor: '#EF4444',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 500
    },
    secondaryButton: {
      padding: '8px 16px',
      backgroundColor: '#F3F4F6',
      color: '#374151',
      border: '1px solid #D1D5DB',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 500
    },
    badge: {
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: '9999px',
      fontSize: '12px',
      fontWeight: 500
    },
    greenBadge: {
      backgroundColor: '#D1FAE5',
      color: '#059669'
    },
    redBadge: {
      backgroundColor: '#FEE2E2',
      color: '#DC2626'
    },
    progressBar: {
      height: '8px',
      backgroundColor: '#E5E7EB',
      borderRadius: '4px',
      overflow: 'hidden'
    },
    progressFill: {
      height: '100%',
      backgroundColor: '#4A90E2',
      transition: 'width 0.3s ease'
    },
    blockForm: {
      display: 'flex',
      gap: '12px',
      alignItems: 'center',
      marginBottom: '20px'
    },
    select: {
      padding: '8px 12px',
      border: '1px solid #D1D5DB',
      borderRadius: '6px',
      fontSize: '14px'
    }
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <Header />
        <div style={styles.content}>
          <p>Loading rate limit data...</p>
        </div>
      </div>
    )
  }

  return (
    <div style={styles.container}>
      <Header />
      <div style={styles.content}>
        <h1 style={styles.title}>Rate Limiting Dashboard</h1>
        <p style={styles.subtitle}>Monitor and manage API rate limits</p>

        {error && (
          <div style={{ ...styles.card, backgroundColor: '#FEE2E2', color: '#DC2626' }}>
            {error}
            <button onClick={() => setError(null)} style={{ marginLeft: '10px' }}>Dismiss</button>
          </div>
        )}

        <div style={styles.tabs}>
          {(['overview', 'config', 'blocked', 'users'] as const).map((tab) => (
            <button
              key={tab}
              style={{ ...styles.tab, ...(activeTab === tab ? styles.activeTab : {}) }}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && statistics && (
          <>
            <div style={{ marginBottom: '16px', display: 'flex', gap: '12px', alignItems: 'center' }}>
              <span>Time Range:</span>
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(Number(e.target.value))}
                style={styles.select}
              >
                <option value={15}>Last 15 minutes</option>
                <option value={60}>Last hour</option>
                <option value={360}>Last 6 hours</option>
                <option value={1440}>Last 24 hours</option>
              </select>
              <button onClick={loadData} style={styles.secondaryButton}>Refresh</button>
            </div>

            <div style={styles.statsGrid}>
              <div style={styles.statCard}>
                <div style={styles.statValue}>{statistics.total_requests.toLocaleString()}</div>
                <div style={styles.statLabel}>Total Requests</div>
              </div>
              <div style={styles.statCard}>
                <div style={{ ...styles.statValue, color: '#059669' }}>
                  {statistics.allowed_requests.toLocaleString()}
                </div>
                <div style={styles.statLabel}>Allowed Requests</div>
              </div>
              <div style={styles.statCard}>
                <div style={{ ...styles.statValue, color: '#DC2626' }}>
                  {statistics.blocked_requests.toLocaleString()}
                </div>
                <div style={styles.statLabel}>Blocked Requests</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statValue}>{statistics.block_rate}%</div>
                <div style={styles.statLabel}>Block Rate</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statValue}>{statistics.currently_blocked_ips}</div>
                <div style={styles.statLabel}>Blocked IPs</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statValue}>{statistics.active_buckets}</div>
                <div style={styles.statLabel}>Active Buckets</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              <div style={styles.card}>
                <h3 style={styles.cardTitle}>Requests by Endpoint Type</h3>
                {Object.entries(statistics.by_endpoint).map(([endpoint, stats]) => (
                  <div key={endpoint} style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{ fontWeight: 500 }}>{endpoint}</span>
                      <span style={{ color: '#6B7280' }}>{stats.total} requests</span>
                    </div>
                    <div style={styles.progressBar}>
                      <div
                        style={{
                          ...styles.progressFill,
                          width: `${stats.total > 0 ? (stats.allowed / stats.total) * 100 : 0}%`,
                          backgroundColor: stats.blocked > 0 ? '#F59E0B' : '#10B981'
                        }}
                      />
                    </div>
                    <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                      {stats.allowed} allowed, {stats.blocked} blocked
                    </div>
                  </div>
                ))}
              </div>

              <div style={styles.card}>
                <h3 style={styles.cardTitle}>Top Blocked Identifiers</h3>
                {statistics.top_blocked_identifiers.length === 0 ? (
                  <p style={{ color: '#6B7280' }}>No blocked requests in this time range</p>
                ) : (
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Identifier</th>
                        <th style={styles.th}>Blocked Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statistics.top_blocked_identifiers.map((item, idx) => (
                        <tr key={idx}>
                          <td style={styles.td}>{item.identifier}</td>
                          <td style={styles.td}>
                            <span style={{ ...styles.badge, ...styles.redBadge }}>
                              {item.blocked_count}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}

        {activeTab === 'config' && config && (
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>Rate Limit Configuration</h3>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Endpoint Type</th>
                  <th style={styles.th}>Description</th>
                  <th style={styles.th}>Requests</th>
                  <th style={styles.th}>Window (seconds)</th>
                  <th style={styles.th}>Rate</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(config.limits).map(([endpoint, limit]) => (
                  <tr key={endpoint}>
                    <td style={{ ...styles.td, fontWeight: 500 }}>{endpoint}</td>
                    <td style={styles.td}>{config.description[endpoint] || '-'}</td>
                    <td style={styles.td}>
                      {editingEndpoint === endpoint ? (
                        <input
                          type="number"
                          value={editRequests}
                          onChange={(e) => setEditRequests(Number(e.target.value))}
                          style={styles.input}
                        />
                      ) : (
                        limit.requests
                      )}
                    </td>
                    <td style={styles.td}>
                      {editingEndpoint === endpoint ? (
                        <input
                          type="number"
                          value={editWindow}
                          onChange={(e) => setEditWindow(Number(e.target.value))}
                          style={styles.input}
                        />
                      ) : (
                        limit.window
                      )}
                    </td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, ...styles.greenBadge }}>
                        {(limit.requests / (limit.window / 60)).toFixed(1)}/min
                      </span>
                    </td>
                    <td style={styles.td}>
                      {editingEndpoint === endpoint ? (
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => handleUpdateConfig(endpoint)}
                            style={styles.button}
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingEndpoint(null)}
                            style={styles.secondaryButton}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            setEditingEndpoint(endpoint)
                            setEditRequests(limit.requests)
                            setEditWindow(limit.window)
                          }}
                          style={styles.secondaryButton}
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'blocked' && (
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>Blocked IP Addresses</h3>

            <div style={styles.blockForm}>
              <input
                type="text"
                placeholder="IP Address"
                value={blockIP}
                onChange={(e) => setBlockIP(e.target.value)}
                style={{ ...styles.input, width: '200px' }}
              />
              <select
                value={blockDuration}
                onChange={(e) => setBlockDuration(Number(e.target.value))}
                style={styles.select}
              >
                <option value={15}>15 minutes</option>
                <option value={60}>1 hour</option>
                <option value={360}>6 hours</option>
                <option value={1440}>24 hours</option>
                <option value={10080}>1 week</option>
              </select>
              <button onClick={handleBlockIP} style={styles.dangerButton}>
                Block IP
              </button>
            </div>

            {blockedIPs.length === 0 ? (
              <p style={{ color: '#6B7280' }}>No IPs currently blocked</p>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>IP Address</th>
                    <th style={styles.th}>Blocked Until</th>
                    <th style={styles.th}>Remaining Time</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {blockedIPs.map((ip) => (
                    <tr key={ip.ip}>
                      <td style={{ ...styles.td, fontFamily: 'monospace' }}>{ip.ip}</td>
                      <td style={styles.td}>
                        {new Date(ip.blocked_until).toLocaleString()}
                      </td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, ...styles.redBadge }}>
                          {formatDuration(ip.remaining_seconds)}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <button
                          onClick={() => handleUnblockIP(ip.ip)}
                          style={styles.secondaryButton}
                        >
                          Unblock
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>User-Specific Rate Limits</h3>
            <p style={{ color: '#6B7280', marginBottom: '16px' }}>
              Configure custom rate limits for specific users. Users without custom limits
              will use the default configuration.
            </p>
            <p style={{ color: '#9CA3AF', fontStyle: 'italic' }}>
              User rate limit management interface coming soon...
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default RateLimitDashboard
